<template>
    <div>
        <VJoyPanel
            :id="anchorsPageProfile.microConsultingSection"
            class="micro-consulting-section"
            padding="large"
            data-testid="micro-consulting-section"
        >
            <template #panel-body>
                <VJoyWrapper
                    class="micro-consulting-section__body"
                    direction="row"
                    align="stretch"
                    column-gap="6"
                    row-gap="4"
                    wrap="nowrap"
                    no-margin
                >
                    <VJoyWrapper class="micro-consulting-section__lead" direction="row" align="flex-start" column-gap="6" wrap="nowrap" no-margin>
                        <MaltIllustration class="micro-consulting-section__illustration" name="group-discussion--high" alt="" width="120" />
                        <VJoyWrapper direction="row" align="stretch" row-gap="3" no-margin>
                            <VJoyWrapper direction="row" wrap="nowrap" row-gap="3" column-gap="3" no-margin>
                                <VJoyTag v-if="!profile?.editMode || !isMicroConsultingActivated" variant="special">
                                    {{ t('general.new') }}
                                </VJoyTag>
                                <VJoyText tag-name="h2" preset="primary-650" class="micro-consulting-section__title">
                                    {{ title }}
                                </VJoyText>
                            </VJoyWrapper>
                            <VJoyText preset="primary-400" class="micro-consulting-section__description">{{ description }}</VJoyText>
                            <VJoyWrapper v-if="!profile?.editMode || !isMicroConsultingActivated" wrap="wrap" column-gap="3" row-gap="2" no-margin>
                                <VJoyTag v-for="(tag, index) in tags" :key="index" variant="inactive" size="small">
                                    <VJoyIcon :name="tag.icon" color="neutral-50" size="xxsmall" />
                                    {{ tag.label }}
                                </VJoyTag>
                            </VJoyWrapper>
                        </VJoyWrapper>
                    </VJoyWrapper>
                    <VJoyWrapper class="micro-consulting-section__actions" direction="column" align="center" justify="center" row-gap="3" no-margin>
                        <VJoyButton
                            :disabled="!isHydrated"
                            :data-testid="buttonDataTestId"
                            :variant="buttonVariant"
                            size="small"
                            :label="buttonLabel"
                            rel="nofollow"
                            @click="redirectToMicroConsulting"
                        />
                        <VJoyWrapper direction="row" align="center" column-gap="1" row-gap="0" wrap="nowrap" no-margin>
                            <VJoyIcon
                                v-if="profile?.editMode"
                                name="info-circle"
                                :color="isMicroConsultingActivated ? 'secondary' : 'neutral-50'"
                                size="xxsmall"
                                aria-hidden="true"
                            />
                            <VJoyText
                                v-if="hintLabel"
                                preset="primary-200"
                                class="micro-consulting-section__visibility-hint"
                                :class="{'micro-consulting-section__visibility-hint--active': isMicroConsultingActivated && profile?.editMode}"
                                v-html="hintLabel"
                            />
                        </VJoyWrapper>
                    </VJoyWrapper>
                </VJoyWrapper>
                <VJoyHighlight
                    v-if="profile?.editMode && !isFreelancerEligible"
                    data-testid="micro-consulting-eligibility-warning-highlight"
                    level="warning"
                    display-icon
                    icon="warning-triangle"
                >
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="t('profile.section.micro.consulting.warning.freelancer.not.eligible', [freelancerCompanyId])" />
                </VJoyHighlight>
            </template>
        </VJoyPanel>
        <LazyAmplitudeSessionReplayStarter v-if="$ff('amplitude-recording-for-micro-consulting')" />
    </div>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {LazyAmplitudeSessionReplayStarter, MaltIllustration} from '#components';
    import {navigateTo, useFormat, useHydrationState, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import type {TJoyIconsNames} from '@maltjoy/core-vue';
    import {VJoyButton, VJoyHighlight, VJoyIcon, VJoyPanel, VJoyTag, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import {useMarkdownToHtml} from '~/composables/useMarkdownToHtml';
    import {anchorsPageProfile} from '~/config/anchors';
    import {useMicroConsultingStore} from '~/store/micro-consulting/microConsultingStore';
    import {useProfileStore} from '~/store/profileStore';

    const {t} = useTranslation();
    const {formatAmount} = useFormat();
    const {markdownToText} = useMarkdownToHtml();
    const {isHydrated} = useHydrationState();

    const isEnableMicroConsultingBookingEnabled = useFeatureFlag('enable-micro-consulting-booking');
    const microConsultingStore = useMicroConsultingStore();
    const {
        isMicroConsultingActivated,
        hourlyRate,
        currency,
        description: freelancerDescription,
        isFreelancerEligible,
        freelancerCompanyId,
    } = storeToRefs(microConsultingStore);

    const {profile} = storeToRefs(useProfileStore());

    const title = computed(() => {
        if (!profile.value?.editMode) {
            return t('profile.section.micro.consulting.client.title');
        }
        return isMicroConsultingActivated.value
            ? t('profile.section.micro.consulting.freelancer.title.active')
            : t('profile.section.micro.consulting.freelancer.title.not.active');
    });

    const MICRO_CONSULTING_DESCRIPTION_PREVIEW_LENGTH = 200;
    const description = computed(() => {
        if (!profile.value?.editMode) {
            const freelancerDescriptionAsText = markdownToText(freelancerDescription.value);
            if (!freelancerDescriptionAsText) {
                return t('profile.section.micro.consulting.client.text');
            }
            return freelancerDescriptionAsText.length > MICRO_CONSULTING_DESCRIPTION_PREVIEW_LENGTH
                ? `${freelancerDescriptionAsText.slice(0, MICRO_CONSULTING_DESCRIPTION_PREVIEW_LENGTH)}...`
                : freelancerDescriptionAsText;
        }
        return isMicroConsultingActivated.value
            ? isEnableMicroConsultingBookingEnabled.value
                ? t('profile.section.micro.consulting.freelancer.text.active')
                : t('profile.section.micro.consulting.freelancer.text.active.client.ff.disabled')
            : t('profile.section.micro.consulting.freelancer.text.not.active');
    });

    const buttonDataTestId = computed(() => {
        if (!profile.value?.editMode) {
            return 'micro-consulting-section-book-a-call-button';
        }
        return isMicroConsultingActivated.value ? 'micro-consulting-section-manage-button' : 'micro-consulting-section-setup-button';
    });
    const buttonLabel = computed(() => {
        if (!profile.value?.editMode) {
            return t('profile.section.micro.consulting.client.button.label');
        }
        return isMicroConsultingActivated.value
            ? t('profile.section.micro.consulting.freelancer.button.active')
            : t('profile.section.micro.consulting.freelancer.button.not.active');
    });

    const buttonVariant = computed(() => {
        if (!profile.value?.editMode) {
            return 'primary';
        }
        return 'secondary';
    });

    const hintLabel = computed(() => {
        if (!profile.value?.editMode) {
            if (!currency.value || !hourlyRate.value) {
                return undefined;
            }
            return t('profile.section.micro.consulting.client.hint.label', {
                0: formatAmount(currency.value, hourlyRate.value),
            });
        }
        return isEnableMicroConsultingBookingEnabled.value && isMicroConsultingActivated.value
            ? t('profile.section.micro.consulting.visibility.hint.active')
            : t('profile.section.micro.consulting.visibility.hint');
    });

    const tags = computed<{icon: TJoyIconsNames; label: string}[]>(() => {
        if (!profile.value?.editMode) {
            return [
                {icon: 'camcorder', label: t('profile.section.micro.consulting.client.tag.video.call')},
                {icon: 'calendar', label: t('profile.section.micro.consulting.client.tag.calendar')},
                {icon: 'star', label: t('profile.section.micro.consulting.client.tag.instant.value')},
            ];
        }
        return [
            {icon: 'dollar-circle', label: t('profile.section.micro.consulting.tag.hourly.rate')},
            {icon: 'calendar', label: t('profile.section.micro.consulting.tag.share.calendar')},
            {icon: 'thunder', label: t('profile.section.micro.consulting.tag.auto.payments')},
        ];
    });

    function redirectToMicroConsulting() {
        if (!profile.value?.editMode) {
            return navigateTo(`/micro-consulting/${profile.value?.identityId}`, {external: true});
        }
        return navigateTo(`/micro-consulting/edition`, {external: true});
    }
</script>
<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .micro-consulting-section {
        &__body {
            @include mq.screen_xs {
                flex-direction: column;
            }
        }

        &__illustration {
            @include mq.screen_xs {
                display: none;
            }
        }

        &__lead {
            flex: 1 1 auto;
            min-width: 0;
        }

        &__actions {
            flex: 0 0 auto;
        }

        &__title,
        &__description {
            overflow-wrap: anywhere;
        }

        &__visibility-hint {
            text-align: center;

            &--active {
                color: var(--joy-color-secondary-50);
            }
        }
    }
</style>
